<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('maintenanceSchedule.activities') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <VDraggable
        v-if="hasPermission('maintenanceSchedules.activities.view') && hasPermission('nodes.types.view')"
        ref="tableRemoteRef"
        :items="displayedActivities"
        :actions="actions"
        :headers="tableHeaders"
        :pagination="pagination"
        :title="$t('maintenanceSchedule.activities')"
        iconHeader="add"
        :filters="filters"
        :filterName="filterName"
        :activities="activities"
        :handleSearch="handleSearch"
        :handleApplyFilters="handleApplyFilters"
        :hideDefaultFooter="true"
        :isSearchable="false"
        :isCustomIconNext="true"
        :isInfinitScroll="true"
        :sortableColumns="[
          'displayOrder',
          'name',
          'networkTypes',
          'category',
          'frequency',
        ]"
        :notSortableColumns="[
          'procedure',
          'networkTypes',
          'componentTypes',
          'actions'
        ]"
        @sendQueryParams="fetchQuery"
        @change="onDragChange"
        @onClickAction="onClickAction"
        @functionIconHeader="onClickAdd"
      />
      <div v-intersect="endIntersect" />
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { pick, isEmpty } from 'lodash';
import ActivityForm from '@/components/forms/ActivityForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VDraggable from '@/components/shared/content-views/VDraggable';

export default {
    components: {
        BoxWithTitle,
        VDraggable,
    },
    data() {
        return {
            activities: [],
            pagination: {},
            data: {
                networkTypes: [],
                componentTypes: [],
                categories: [],
            },
            queryParams: {
                perPage: 25,
                search: this.search,
            },
            filterName: 'activity',
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'teamMainLang',
        ]),
        filters() {
            return [
                {
                    key: 'networkTypes',
                    label: 'forms.labels.networkTypes',
                    type: 'select',
                    multiple: true,
                    items: this.$utils.ascSort(Object.values(this.data.networkTypes)),
                },
                {
                    key: 'componentTypes',
                    label: 'forms.labels.componentTypes',
                    type: 'select',
                    multiple: true,
                    items: this.$utils.ascSort(Object.values(this.data.componentTypes)),
                },
                {
                    key: 'categories',
                    label: 'forms.labels.category',
                    type: 'select',
                    multiple: true,
                    items: this.$utils.ascSort(Object.values(this.data.categories)),
                },
            ];
        },
        tableHeaders() {
            return [
                'displayOrder',
                'name',
                'category',
                'frequency',
                'networkTypes',
                'componentTypes',
                'procedure',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
                sortable: // List of not sortable columns here (procedure, componentTypes, networkTypes)
                    header !== 'procedure'
                    && header !== 'componentTypes'
                    && header !== 'networkTypes',
            }));
        },
        displayedActivities() {
            return this.activities.map(activity => {
                const {
                    activityName:
                    name,
                    category,
                    frequency,
                    componentTypes,
                    networkTypes,
                    procedure,
                    displayOrder,
                } = this.repareActivityToDisplay(activity);

                return {
                    id: activity.id,
                    name,
                    category,
                    frequency,
                    componentTypes,
                    networkTypes,
                    procedure,
                    displayOrder,
                };
            });
        },
        actions() {
            const actions = [];
            if (this.hasPermission('maintenanceSchedules.activities.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }
            if (this.hasPermission('maintenanceSchedules.activities.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    async created() {
        await this.getActivities();
        await this.getNetworkTypes();
        await this.getComponentTypes();
        await this.getCategories();
    },
    methods: {
        async getActivities() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getActivities(this.queryParams);
                this.activities = [...this.activities, ...data.activities];
                this.pagination = data.pagination;
            }
        },

        async fetchQuery({ order, ...queryParams }) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                search: queryParams?.search || '',
                networkTypes: queryParams?.networkTypes || [],
                componentTypes: queryParams?.componentTypes || [],
                categories: queryParams?.categories || [],
                page: 1,
                order,
            };
            this.pagination = {};
            this.activities = [];
            await this.getActivities();
        },

        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            this.fetchQuery(this.queryParams);
        },

        async handleApplyFilters(filterParams) {
            this.queryParams = {
                ...this.queryParams,
                ...filterParams,
                search: filterParams?.search || '',
                networkTypes: !filterParams.networkTypes ? [] : filterParams.networkTypes.join(','),
                componentTypes: !filterParams.componentTypes ? [] : filterParams.componentTypes.join(','),
                categories: !filterParams.categories ? [] : filterParams.categories.join(','),
            };

            await this.fetchQuery(this.queryParams);
        },
        async getNetworkTypes() {
            const { data } = await this.$rq.getNetworkTypes();
            const dataNetworkTypes = this.filterDataMix(data.networkTypes);
            this.data.networkTypes = [...dataNetworkTypes];
        },

        async getComponentTypes() {
            const { data } = await this.$rq.getComponentTypes();
            const dataComponentTypes = this.filterDataMix(data.componentTypes);
            this.data.componentTypes = [...dataComponentTypes];
        },

        async getCategories() {
            const { data } = await this.$rq.getMaintenanceScheduleCategories({ pagination: false });
            const dataCategories = data.categories.map(({ id, name }) => ({
                text: `${name[this.userLang] || name[this.teamMainLang]}`,
                value: id,
                name: `${name[this.userLang] || name[this.teamMainLang]}`,
            }));
            this.data.categories = [...dataCategories];
        },

        filterDataMix(filterData) {
            return filterData?.map(({ key, name }) => ({
                text: `${name[this.userLang] || name[this.teamMainLang]}`,
                value: key,
                name: `${name[this.userLang] || name[this.teamMainLang]}`,
            }));
        },

        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('maintenanceSchedule.addActivity'),
                component: Vue.component('ActivityForm', ActivityForm),
                showLangs: true,
                onConfirm: requestPayload => this.addActivity(requestPayload),
            });
        },
        async onClickAction(activity, action) {
            if (activity === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'edit') {
                const data = this.activities.find(ac => ac.id === activity.id);
                const activityInfo = this.prepareActivity(data);

                this.$root.$emit('showModal', {
                    title: this.$t('maintenanceSchedule.updateActivity'),
                    component: Vue.component('ActivityForm', ActivityForm),
                    payload: { activity: activityInfo, isEdition: true },
                    showLangs: true,
                    onConfirm: requestPayload => this.updateActivity(activity.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('maintenanceSchedule.removeFrequency'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('maintenanceSchedule.confirmRemoveActivity') },
                    onConfirm: () => this.removeActivity(activity),
                    isForm: false,
                });
            } else if (activity === 'add') {
                this.onClickAdd();
            }
        },
        prepareActivity(activity) {
            const preparedActivity = {};
            const {
                category,
                componentTypes,
                frequency,
                networkTypes,
                procedure,
            } = activity;

            preparedActivity.category = category.id;

            if (frequency) {
                preparedActivity.frequency = frequency.id;
            }

            if (procedure) {
                preparedActivity.procedure = procedure.id;
            }

            if (componentTypes) {
                preparedActivity.componentTypes = componentTypes.map(({ id }) => id);
            }

            if (networkTypes) {
                preparedActivity.networkTypes = networkTypes.map(({ id }) => id);
            }

            return { ...activity, ...preparedActivity };
        },
        preparePayload(requestPayload) {
            return pick(requestPayload, [
                'name',
                'category',
                'frequency',
                'procedure',
                'componentTypes',
                'networkTypes',
            ]);
        },
        repareActivityToDisplay(activity) {
            const activityName = activity.name[this.userLang] && activity.name[this.userLang].length
                ? activity.name[this.userLang] : activity.name[this.teamMainLang];

            const { name: catName } = activity.category;

            const category = catName[this.userLang] && catName[this.userLang].length
                ? catName[this.userLang] : catName[this.teamMainLang];

            let frequency = null;
            if (activity.frequency) {
                const { name: frequencyName } = activity?.frequency;
                frequency = frequencyName[this.userLang] && frequencyName[this.userLang].length
                    ? frequencyName[this.userLang] : frequencyName[this.teamMainLang];
            }

            let componentTypes = null;

            if (activity.componentTypes) {
                componentTypes = activity.componentTypes.map(({ name }) => (name[this.userLang] && name[this.userLang].length
                    ? name[this.userLang] : name[this.teamMainLang])).sort();
            } else componentTypes = [this.$t('forms.labels.any')];

            let networkTypes = null;
            if (activity.networkTypes) {
                networkTypes = activity.networkTypes.map(({ name }) => (name[this.userLang] && name[this.userLang].length
                    ? name[this.userLang] : name[this.teamMainLang])).sort();
            } else networkTypes = [this.$t('forms.labels.any')];

            const procedure = activity?.procedure?.files ? activity.procedure?.files : [];

            const displayOrder = activity.displayOrder + 1;

            return { displayOrder, activityName, category, frequency, componentTypes, networkTypes, procedure };
        },
        async addActivity(requestPayload) {
            let payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            payload = this.preparePayload(requestPayload);

            const { data } = await this.$rq.addActivity(payload);
            const { name: activityName } = data;
            this.$toasted.global.toast_success({
                message: this.$t('msg.activityAdded', { activityName: activityName[this.userLang] || activityName[this.teamMainLang] }),
            });
            await this.fetchQuery({});
        },
        async updateActivity(id, requestPayload) {
            if (!requestPayload) return;
            const payload = this.preparePayload(requestPayload);

            const { data } = await this.$rq.updateActivity(id, payload);
            const { name: activityName } = data;
            this.$toasted.global.toast_success({
                message: this.$t('msg.activityUpdated', { activityName: activityName[this.userLang] || activityName[this.teamMainLang] }),
            });
            await this.fetchQuery({});
        },
        async removeActivity(activity) {
            await this.$rq.removeActivity(activity.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.activityRemoved', { activityName: activity.name }),
            });
            await this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getActivities();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        async onDragChange(newIndex) {
            const bodyParams = { newIndex: newIndex.newIndex + 1 };
            await this.$rq.updateActivitiesPosition(newIndex.activityId, bodyParams);
            this.resetData();
            this.getActivities();
        },

        resetData() {
            this.activities = [];
            this.queryParams = { perPage: 25 };
            this.pagination = {};
        },


    },
};
</script>
